// filepath: resources/js/components/pages/CMSPages/FriVia/Results/QuizResultsTable.jsx
import React from 'react';
import PropTypes from 'prop-types';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridTextSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useTranslation } from 'react-i18next';
import {
  useYesNoStringDataGridFilter,
} from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useUniqueOptionsDataGridFilter } from '../../../../common/DataGrid/filters/useUniqueOptionsDataGridFilter.jsx';

const QuizResultsTable = ({ quizId, questionId, rows, isQuestionMode }) => {
  const { t } = useTranslation();

  // Filters
  const surnameFilter = useDataGridTextFilter('last_name');
  const uidNumberFilter = useDataGridTextFilter('uidnumber');
  const nameFilter = useDataGridTextFilter('first_name');
  const groupFilter = useUniqueOptionsDataGridFilter('study_group', rows);
  const yesNoFilter = isQuestionMode
    ? useYesNoStringDataGridFilter('answered_correctly')
    : null;
  const studentAnswerFilter = isQuestionMode
    ? useDataGridTextFilter('student_answers_text')
    : null;
  const ipAddressFilter = isQuestionMode
    ? useDataGridTextFilter('ip_address')
    : null;

  const baseColumns = [
    {
      title: t('Priezvisko'),
      dataIndex: 'last_name',
      sorter: dataGridTextSorter('last_name'),
      ...surnameFilter,
      ellipsis: true,
      defaultSortOrder: 'ascend',
    },
    {
      title: t('Meno'),
      dataIndex: 'first_name',
      sorter: dataGridTextSorter('first_name'),
      ...nameFilter,
      ellipsis: true,
    },
    {
      title: t('Osobné číslo'),
      dataIndex: 'uidnumber',
      sorter: dataGridTextSorter('uidnumber'),
      ...uidNumberFilter,
      ellipsis: true,
      render: (value) => (value === null || value === undefined || value === '' ? '-' : String(value)),
    },
    {
      title: t('Študijná skupina'),
      dataIndex: 'study_group',
      sorter: dataGridTextSorter('study_group'),
      width: 200,
      ...groupFilter,
      ellipsis: true,
    },
  ];

  const modeColumn = isQuestionMode
    ? {
        title: t('Odpovedal správne'),
        dataIndex: 'answered_correctly',
        sorter: dataGridTextSorter('answered_correctly'),
        width: 250,
        ...yesNoFilter,
        render: (val) => {
          const yesLike = val === 'Yes';
          return yesLike ? t('Áno') : t('Nie');
        },
      }
    : {
        title: t('Počet správnych odpovedí'),
        dataIndex: 'correct_count',
        key: 'correct_count',
        width: 250,
        sorter: (a, b) => {
          const valA = Number(a?.correct_count ?? 0);
          const valB = Number(b?.correct_count ?? 0);
          return valA - valB;
        },
      };

  const columns = isQuestionMode
    ? [
        ...baseColumns,
        {
          title: t('IP adresa'),
          dataIndex: 'ip_address',
          key: 'ip_address',
          ellipsis: true,
          sorter: dataGridTextSorter('ip_address'),
          ...(ipAddressFilter || {}),
          render: (v) => (v && String(v).trim() !== '' ? v : '-'),
        },
        {
          title: t('Odpoveď študenta'),
          dataIndex: 'student_answers_text',
          ellipsis: true,
          sorter: dataGridTextSorter('student_answers_text'),
          ...(studentAnswerFilter || {}),
          render: (v) => (v && v.trim() !== '' ? v : '-'),
        },
        modeColumn,
      ]
    : [...baseColumns, modeColumn];

  return (
    <DataGrid
      rowKey={(row) =>
        `${row.user_id}-${isQuestionMode ? questionId : (row.quiz_id ?? quizId)}`
      }
      columns={columns}
      dataSource={rows}
      permissionsBasic={'quiz-student-answers'}
      uniqueTableId={`quiz-results-${isQuestionMode ? 'question-' + questionId : 'quiz-' + quizId}`}
      disableExpandRow={() => true}
      allowClick={false}
      disableSettings
    />
  );
};

QuizResultsTable.propTypes = {
  quizId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rows: PropTypes.array,
  mode: PropTypes.oneOf(['quiz', 'question']),
};

export default QuizResultsTable;
